import { useCallback, useEffect } from 'react';
import authorizationManager from '@utils/authorizationManager';
import { StatusCodes } from '@constants/enum/common';
import { FeatureFlag } from '@constants/featureFlags';
import {
  userFetchUserPermissionsStatusSelector,
  userPermissionsSelector,
} from '@redux/selectors/userSelectors';
import { PermissionAction, PermissionAsset } from '@redux/types/types';
import { useAppSelector } from './redux';
import { useStorageFeatureFlag } from './useStorageFeatureFlag';

/**
 * A hook that provides a function to check user permissions.
 * This hook centralizes access to the AuthorizationManager and subscribes to Redux
 * to ensure components react to permission changes.
 *
 * @returns An object containing the `hasPermission` function and an `isLoading` boolean.
 *
 * @example
 * ```tsx
 * const { hasPermission, isLoading } = useAuthorization();
 *
 * const canEditPipelines = hasPermission({ [PermissionAsset.PIPELINES]: PermissionAction.WRITE });
 *
 * if (isLoading) return <Spinner />;
 * return canEditPipelines ? <EditButton /> : <ViewButton />;
 * ```
 */
export const useAuthorization = () => {
  const permissions = useAppSelector(userPermissionsSelector);
  const fetchUserPermissionsStatus = useAppSelector(userFetchUserPermissionsStatusSelector);
  const isRbacEnabled = useStorageFeatureFlag(FeatureFlag.RBAC);

  // Sync authorization manager with Redux permissions
  useEffect(() => {
    if (isRbacEnabled && permissions) {
      authorizationManager.setPermissions(permissions);
    }
  }, [permissions, isRbacEnabled]);

  /**
   * Checks if the current user has a specific permission for a given asset.
   *
   * @param requiredPermissions - A record of asset-action pairs.
   * @returns `true` if the user has the required permission(s), `false` otherwise.
   */
  const hasPermission = useCallback(
    (requiredPermissions: Partial<Record<PermissionAsset, PermissionAction>>): boolean => {
      if (!isRbacEnabled) return true;

      // Ensure permissions are synced before checking
      if (permissions && permissions.length > 0) {
        authorizationManager.setPermissions(permissions);
      }

      return authorizationManager.has(requiredPermissions);
    },
    [permissions, isRbacEnabled],
  );

  return { hasPermission, isLoading: fetchUserPermissionsStatus === StatusCodes.IN_PROGRESS };
};
